import { useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { SelectField, Options } from '../components/Field.jsx';
import { Loader, ErrorAlert, EmptyRow, InfoAlert } from '../components/States.jsx';
import { useGetStockStatusQuery } from '../store/apiSlice.js';
import { useReferenceData } from '../store/useReferenceData.js';

function ChipRow({ label, items, selected, onToggle }) {
  return (
    <>
      <div className="filter-label">{label}</div>
      <div className="chip-row">
        {items.length === 0 ? (
          <span className="muted" style={{ fontSize: 11 }}>
            None added yet
          </span>
        ) : (
          items.map((item) => {
            const on = selected.includes(item);
            return (
              <button
                key={item}
                type="button"
                className={`chip${on ? ' on' : ''}`}
                aria-pressed={on}
                onClick={() => onToggle(item)}
              >
                {item}
              </button>
            );
          })
        )}
      </div>
    </>
  );
}

export default function StockStatusReportPage() {
  const { products, textures, lengths, colors, vendors } = useReferenceData();

  const [vendor, setVendor] = useState('');
  const [product, setProduct] = useState('');
  const [filters, setFilters] = useState({ textures: [], lengths: [], colors: [] });

  const toggle = (group) => (value) =>
    setFilters((f) => ({
      ...f,
      [group]: f[group].includes(value) ? f[group].filter((v) => v !== value) : [...f[group], value],
    }));


  // No product chosen means there is nothing to ask for, so the query is skipped.
  const {
    data,
    isFetching: loading,
    error: apiError,
    refetch: reload,
  } = useGetStockStatusQuery(
    { product, vendor, textures: filters.textures, lengths: filters.lengths, colors: filters.colors },
    { skip: !product }
  );

  const error = apiError?.message ?? null;
  const rows = data?.rows ?? [];
  const meta = data?.meta ?? null;

  const clearAll = () => {
    setVendor('');
    setProduct('');
    setFilters({ textures: [], lengths: [], colors: [] });
  };

  return (
    <>
      <PageHeader
        eyebrow="Reports"
        title="Stock Status Report"
        subtitle="Filter by vendor and product, then narrow down by texture, length or color using the chips."
      />

      <Card title="Filters" tag="Multi-select supported">
        <div className="form-grid">
          <SelectField label="Vendor" value={vendor} onChange={(e) => setVendor(e.target.value)}>
            <Options items={vendors} placeholder="All vendors" />
          </SelectField>
          <SelectField label="Product Name" value={product} onChange={(e) => setProduct(e.target.value)}>
            <Options items={products} placeholder="Select product" />
          </SelectField>
        </div>

        <ChipRow label="Filter by Texture" items={textures} selected={filters.textures} onToggle={toggle('textures')} />
        <ChipRow label="Filter by Length" items={lengths} selected={filters.lengths} onToggle={toggle('lengths')} />
        <ChipRow label="Filter by Color" items={colors} selected={filters.colors} onToggle={toggle('colors')} />

        <div className="btn-row">
          <button type="button" className="btn ghost" onClick={clearAll}>
            Clear filters
          </button>
        </div>
      </Card>

      {meta?.vendorScoped && (
        <InfoAlert>
          Showing quantities <strong>received from {meta.vendor}</strong>. Sales are not linked to a vendor, so the
          Sold column is only meaningful with the vendor filter cleared.
        </InfoAlert>
      )}

      <Card title="Stock Status" tag="Grouped by Length / Texture / Color">
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>Length</th>
                    <th>Texture</th>
                    <th>Color</th>
                    <th>Received</th>
                    <th>Sold</th>
                    <th>Available Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {!product ? (
                    <EmptyRow colSpan={6}>Select a product to view its stock status</EmptyRow>
                  ) : !rows.length ? (
                    <EmptyRow colSpan={6}>No stock matches the selected filters</EmptyRow>
                  ) : (
                    rows.map((row) => (
                      <tr key={`${row.length}|${row.texture}|${row.color}`}>
                        <td>{row.length || '—'}</td>
                        <td>{row.texture || '—'}</td>
                        <td>{row.color || '—'}</td>
                        <td className="num">{row.qtyIn}</td>
                        <td className="num">{meta?.vendorScoped ? '—' : row.qtyOut}</td>
                        <td className="num">{row.available}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {product && rows.length > 0 && (
              <div className="grand-total-line">Total available: {meta?.totalAvailable ?? 0} units</div>
            )}
          </>
        )}
      </Card>
    </>
  );
}
